import { InfoOutlined } from "@mui/icons-material"
import { Box, Fade, Grid, Link, Typography } from "@mui/material"
import { styled } from "@mui/styles"
import { get } from "lodash"
import { useEffect, useState } from "react"
import { FormattedMessage } from "react-intl"
import { useAuth } from "react-oidc-context"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router"
import InfoLabel from "~/components/InfoLabel/InfoLabel"
import { API_SERVER_USER } from "~/constants/api"
import { lightblue } from "~/constants/colors"
import LoginForm from "~/containers/Login"
import { PlaneusCompleteLogo } from "~/theme/Planeus"
import { get as getHttp } from "~/utils/http"
import { OIDC_LOGOUT_URL } from "../../oidcConfig"
import { appInit } from "../../actions/app"

const PlaneusRoot = styled(Box)({
  display: "flex",
  width: "100vw",
  height: "100vh",
  flexGrow: 1,
  backgroundColor: lightblue,
  background: "linear-gradient(150deg, #00a5e5 -10%, #00346e 100%)",
})

const StyledInfoText = styled(Typography)({
  color: "#FFF",
  fontSize: 18,
})

const StyledInfoContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
})

const StyledInfoWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
})

const StyledInfoOutlinedIcon = styled(InfoOutlined)({
  marginRight: 4,
  alignSelf: "flex-start",
  color: "#FFF",
})

const Login = () => {
  const location = useLocation()
  const auth = useAuth()
  const dispatch = useDispatch()
  const [authCode, setAuthCode] = useState(null)
  const [has2FA, setHas2FA] = useState(false)
  const [loading, setLoading] = useState(true)
  const [errorDescription, setErrorDescription] = useState(null)
  const authenticated = useSelector(state => get(state, "user.authentication.authenticated"))

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const code = queryParams.get("code")
    if (code) {
      setAuthCode(code)
    }
  }, [location])

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    const errorMessage = queryParams.get("error_description")

    const code = queryParams.get("code")
    if (code) return

    if (errorMessage) {
      setErrorDescription(errorMessage)
      setLoading(false)
      return
    }

    if (authCode) return

    const fetchHas2FA = async () => {
      if (authenticated) {
        setLoading(false)
        return
      }

      try {
        const fetchData = await getHttp({
          url: `${API_SERVER_USER}/clients/Has2FA`,
          token: null,
        })

        const isBackendStarting = get(fetchData, "backendStarting", false)
        const isRecoveringSales = get(fetchData, "salesRecover", false)
        if (fetchData && !isBackendStarting && !isRecoveringSales) {
          setHas2FA(fetchData)
        } else if (fetchData && (isBackendStarting || isRecoveringSales)) {
          dispatch(appInit(null, isBackendStarting, isRecoveringSales))
        }

        // Only redirect to Auth0 if there is no auth code, the user is not authenticated and user has 2FA
        if (fetchData && !authCode && !authenticated && !isBackendStarting && !isRecoveringSales) {
          const resetPasswordLinkValue = localStorage.getItem("resetPasswordLink")
          if (resetPasswordLinkValue) {
            localStorage.removeItem("resetPasswordLink")
            window.location.href = resetPasswordLinkValue
          } else if (loading) {
            await auth.signinRedirect()
          }
        }
      } catch (e) {
        throw new Error(e)
      } finally {
        setLoading(false)
      }
    }

    fetchHas2FA()
  }, [authenticated, authCode, auth, location])

  if (loading) {
    return (
      <PlaneusRoot>
        <Grid container justifyContent="center" alignItems="center">
          <InfoLabel />
        </Grid>
      </PlaneusRoot>
    )
  }

  return (
    <PlaneusRoot>
      <Grid container justifyContent="center" alignItems="center">
        <InfoLabel />
        <Fade timeout={800} in>
          <Grid item xs={11} sm={8} md={4} lg={3} xl={2}>
            {errorDescription ? (
              <StyledInfoContainer>
                <PlaneusCompleteLogo style={{ width: "90%", marginBottom: 40 }} />
                <StyledInfoWrapper>
                  <StyledInfoOutlinedIcon />
                  <StyledInfoText>
                    <FormattedMessage id={`errors.2FA.${errorDescription}.mainPart1`} /> <br /> <br />
                    <FormattedMessage id={`errors.2FA.${errorDescription}.mainPart2`} />{" "}
                    {errorDescription === "userNotFound" ? <br /> : null}
                    <Link href={OIDC_LOGOUT_URL} underline="always" color="inherit">
                      <FormattedMessage id={`errors.2FA.${errorDescription}.link`} />
                    </Link>
                  </StyledInfoText>
                </StyledInfoWrapper>
              </StyledInfoContainer>
            ) : (
              !has2FA && <LoginForm />
            )}
          </Grid>
        </Fade>
      </Grid>
    </PlaneusRoot>
  )
}

export default Login
